// Brand Console -- Signals.
//
// AF UX audit P1 slice (docs/product/intelligence-console-ux-audit.md,
// Section 5) -- this page is migrated to the shared IntelligenceCard +
// LineageStrip pattern first proven by RecommendationsPage.js (the P0
// slice; see docs/product/intelligence-console-p0-recommendations-report.md).
// No backend/API/data change: this reuses the existing, already-real
// AF-2.2 SignalService entirely through its existing GET
// /intelligence/{workspace_id}/signals route -- no new backend endpoint,
// no new service method, no new collection. "Related opportunity" is
// derived honestly: an OpportunityService document carries its own
// source_signal_ids, so this page cross-references the two lists it
// already fetched rather than inventing a relationship or adding a new
// backend join. Diagnosis count is the same real, already-fetched
// cross-reference as before (a diagnosis carries its own signal_id).
//
// The "Analyze signal" trigger (P2.5's diagnose -> detect-opportunity
// chain) is unchanged: same handler, same two sequential calls to the
// existing AF-2.3/AF-2.4 routes, same isEditor gate, same
// ACTIVE/ACKNOWLEDGED-only eligibility -- only its markup moved from
// SignalCard.jsx into this page's own IntelligenceCard decision slot.
//
// Evidence is shown exactly as before -- a real count, never a new
// preview/drawer -- per this slice's own instruction to reuse only an
// evidence interaction that already exists; Signals never had an
// EvidenceDrawer wired in, so none is added here. It is given its own
// line (rather than folded into the generic metrics row) so it reads as
// a distinct, prominent affordance the way the audit's Section 5
// proposed, without inventing a new click-through.
//
// P1.7: client resolution is still the shared useWorkspaceSelection()
// hook (unchanged from before this migration).

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { DashboardLayout } from '@/components/DashboardLayout';
import { IntelligenceCard } from '@/components/brand/IntelligenceCard';
import { LineageStrip } from '@/components/brand/LineageStrip';
import { StatusBadge, ConfidenceIndicator } from '@/components/brand/Badges';
import { EmptyState, ErrorState, LoadingState } from '@/components/brand/StateViews';
import { WorkspaceSelect } from '@/components/brand/BrandContextBar';
import { useWorkspaceSelection } from '@/hooks/useWorkspaceSelection';
import { useAuth } from '@/contexts/AuthContext';
import { signalsAPI, opportunitiesAPI, diagnosesAPI } from '@/lib/intelligenceApi';

function formatDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

export default function SignalsPage() {
  const { clients, workspaceId, setWorkspaceId, loadingClients } = useWorkspaceSelection();
  const { isEditor } = useAuth();
  const navigate = useNavigate();
  const clientId = workspaceId || null;
  const noClient = !loadingClients && clients.length === 0;
  const [signals, setSignals] = useState(null); // null = loading
  const [opportunityBySignalId, setOpportunityBySignalId] = useState({});
  const [diagnosisCountBySignalId, setDiagnosisCountBySignalId] = useState({});
  const [error, setError] = useState(null);
  // P2.5 -- the signal currently being analyzed (diagnose -> detect
  // opportunity). Only one at a time; the card shows a spinner.
  const [analyzingSignalId, setAnalyzingSignalId] = useState('');

  const load = useCallback(() => {
    if (!clientId) return;
    setSignals(null);
    setError(null);
    Promise.all([
      signalsAPI.listForWorkspace(clientId),
      opportunitiesAPI.listForWorkspace(clientId).catch(() => ({ data: { opportunities: [] } })),
      diagnosesAPI.listForWorkspace(clientId, { status: 'ACTIVE' }).catch(() => ({ data: { diagnoses: [] } })),
    ])
      .then(([signalsRes, opportunitiesRes, diagnosesRes]) => {
        setSignals(signalsRes.data?.signals || []);
        // Real cross-reference only -- an opportunity that genuinely
        // lists this signal's id in its own source_signal_ids, never a
        // guessed/nearest-match association.
        const map = {};
        (opportunitiesRes.data?.opportunities || []).forEach((opp) => {
          (opp.source_signal_ids || []).forEach((sid) => { map[sid] = opp; });
        });
        setOpportunityBySignalId(map);
        // Same discipline for diagnoses: a diagnosis carries its own
        // signal_id (DiagnosisService), so count the real ACTIVE ones per
        // signal rather than inferring.
        const diagCounts = {};
        (diagnosesRes.data?.diagnoses || []).forEach((d) => {
          if (d.signal_id) diagCounts[d.signal_id] = (diagCounts[d.signal_id] || 0) + 1;
        });
        setDiagnosisCountBySignalId(diagCounts);
      })
      .catch((e) => setError(e));
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  // P2.5 -- G1 fix. AF-2.3/AF-2.4's diagnose + detect-opportunity routes
  // have existed and been tested since those milestones but had no
  // founder-facing caller, so a real founder's Signal never reached a
  // Diagnosis or an Opportunity (P2.4 top finding). This is the smallest
  // safe trigger: two sequential calls to those existing, idempotent,
  // tenant-isolated routes, gated on the same isEditor role the
  // RecommendationBridgePanel already uses for its own detect actions.
  // No new backend, no orchestration, no automatic chaining.
  const analyzeSignal = useCallback((signal) => {
    if (!clientId || analyzingSignalId) return;
    setAnalyzingSignalId(signal.id);
    diagnosesAPI.diagnose(clientId, signal.id)
      .then((dRes) => opportunitiesAPI
        .detect(clientId, signal.entity_type, signal.entity_id)
        .then((oRes) => {
          const diagCount = dRes.data?.count ?? 0;
          const oppCount = oRes.data?.count ?? 0;
          const insufficient = oRes.data?.insufficient_evidence;
          const diagPart = `${diagCount} diagnosis hypothes${diagCount === 1 ? 'is' : 'es'}`;
          toast.success(
            insufficient
              ? `Martian analyzed this signal — ${diagPart}. No opportunity meets the evidence threshold yet (see Diagnoses).`
              : `Martian analyzed this signal — ${diagPart}, ${oppCount} opportunit${oppCount === 1 ? 'y' : 'ies'} identified (see Opportunity Portfolio).`,
          );
          load();
        }))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Analysis failed — try again.'))
      .finally(() => setAnalyzingSignalId(''));
  }, [clientId, analyzingSignalId, load]);

  return (
    <DashboardLayout>
      <div data-testid="signals-page" className="max-w-5xl mx-auto">
        <div className="mb-6">
          <p className="text-[11px] font-mono uppercase tracking-wide text-[var(--accent)] mb-1">What changed?</p>
          <h1 className="font-display text-[20px] font-bold text-[var(--text-primary)] mb-1">Signals</h1>
          <p className="text-[12px] text-[var(--text-secondary)] max-w-2xl">Meaningful changes Martian has detected in your data — never a random or estimated fluctuation.</p>
          {isEditor && (
            <p className="text-[11px] text-[var(--text-tertiary)] mt-1">
              Analyze a signal to generate Martian's diagnosis hypotheses and surface any resulting opportunity.
            </p>
          )}
        </div>

        {!noClient && clients.length > 0 && (
          <div className="mb-4">
            <WorkspaceSelect clients={clients} workspaceId={workspaceId} setWorkspaceId={setWorkspaceId} />
          </div>
        )}

        {noClient && (
          <EmptyState title="ADD A BRAND FIRST" description="Signals appear once Martian has a workspace and real data to observe." />
        )}

        {!noClient && signals === null && !error && <LoadingState />}
        {!noClient && error && <ErrorState error={error} onRetry={load} />}

        {!noClient && !error && signals !== null && signals.length === 0 && (
          <EmptyState
            testId="signals-empty-state"
            title="No signals yet"
            description="Martian will surface meaningful changes once enough data is available."
            whyItMatters="Signals are only ever generated from real, connected measurements — never fabricated to fill this page."
          />
        )}

        {!noClient && !error && signals !== null && signals.length > 0 && (
          <div className="space-y-4" data-testid="signals-list">
            {signals.map((signal) => {
              const relatedOpportunity = opportunityBySignalId[signal.id];
              const diagnosisCount = diagnosisCountBySignalId[signal.id] || 0;
              const evidenceCount = Array.isArray(signal.evidence) ? signal.evidence.length : 0;
              // P2.5 -- "Analyze" is the founder's trigger for the
              // diagnose -> detect-opportunity chain. Only meaningful
              // while the signal is still a current concern
              // (ACTIVE/ACKNOWLEDGED); a signal outside those two
              // statuses is history, and OpportunityService.detect only
              // surveys signals still in one of those two statuses. Same
              // gate SignalCard.jsx enforced before.
              const canAnalyze = isEditor && (signal.status === 'ACTIVE' || signal.status === 'ACKNOWLEDGED');
              const analyzing = analyzingSignalId === signal.id;

              // Lineage strip data -- only ever this signal's own real
              // cross-referenced opportunity/diagnosis relationships,
              // matched against already-fetched lists. Omitted entirely
              // when nothing matches; never a placeholder relationship.
              const lineageItems = [];
              if (relatedOpportunity) {
                lineageItems.push({
                  label: 'Related opportunity',
                  value: relatedOpportunity.opportunity_type?.replace(/_/g, ' ') || relatedOpportunity.id,
                  testId: 'lineage-opportunity',
                  onClick: () => navigate('/growth/opportunities'),
                });
              }
              if (diagnosisCount > 0) {
                lineageItems.push({
                  label: 'Diagnosed',
                  value: `${diagnosisCount} hypothes${diagnosisCount === 1 ? 'is' : 'es'} on the Diagnoses page`,
                  testId: 'lineage-diagnoses',
                });
              }

              return (
                <IntelligenceCard
                  key={signal.id}
                  testId={`signal-card-${signal.id}`}
                  stage="SIGNAL"
                  title={signal.title}
                  badges={(
                    <>
                      {signal.severity && <StatusBadge value={signal.severity} />}
                      <StatusBadge value={signal.status} />
                    </>
                  )}
                  statementLabel="What Martian observed"
                  statement={signal.description}
                  metrics={[
                    signal.signal_type ? { label: 'Type', value: signal.signal_type.replace(/_/g, ' ') } : null,
                    signal.entity_type ? { label: 'Entity', value: `${signal.entity_type}${signal.entity_id ? ` · ${signal.entity_id}` : ''}` } : null,
                    signal.metric_key ? { label: 'Metric', value: signal.metric_key } : null,
                    signal.detected_at ? { label: 'Detected', value: formatDate(signal.detected_at) } : null,
                    signal.confidence !== undefined && signal.confidence !== null
                      ? { label: 'Confidence', value: <ConfidenceIndicator value={signal.confidence} />, testId: 'intelligence-card-confidence' }
                      : null,
                  ].filter(Boolean)}
                  lineage={(
                    <>
                      {evidenceCount > 0 && (
                        <p className="text-[11px] font-medium text-[var(--text-secondary)] mb-2" data-testid="signal-evidence-count">
                          {evidenceCount} evidence item{evidenceCount === 1 ? '' : 's'} recorded
                        </p>
                      )}
                      <LineageStrip items={lineageItems} />
                    </>
                  )}
                  decision={canAnalyze && (
                    <button
                      type="button"
                      data-testid="signal-analyze"
                      onClick={() => analyzeSignal(signal)}
                      disabled={analyzing}
                      className="inline-flex items-center gap-1 rounded-[var(--radius-button,6px)] border border-[var(--border)] bg-[var(--surface-sunken,var(--canvas-secondary))] px-2.5 py-1 text-[11px] font-medium text-[var(--text-primary)] hover:bg-[var(--neutral-bg)] disabled:opacity-60"
                    >
                      {analyzing ? 'Analyzing…' : (diagnosisCount > 0 || relatedOpportunity ? 'Re-analyze signal' : 'Analyze signal')}
                    </button>
                  )}
                />
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
